import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";

const REELS = [
  {
    id: "DKjbyYmRqHB",
    url: "https://www.instagram.com/reel/DKjbyYmRqHB/",
  },
  {
    id: "DL2YgUCxYjt",
    url: "https://www.instagram.com/reel/DL2YgUCxYjt/",
  },
  {
    id: "DOoZscEE0h8",
    url: "https://www.instagram.com/reel/DOoZscEE0h8/",
  },
];

/* Portrait reel card. The embed is interactive, so a single tap on the
   play button plays the reel inline. The hover layer is decorative only
   (pointer-events: none) so it never swallows that click. */
function ReelCard({ reel }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  // Bumping this remounts the iframe, resetting the embed so it can be replayed.
  const [reloadKey, setReloadKey] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const replay = () => {
    setIsLoaded(false);
    setReloadKey((k) => k + 1);
  };

  return (
    <div ref={containerRef} className="reel-card">
      {!isLoaded && (
        <div className="reel-loader">
          <div className="reel-spinner" />
        </div>
      )}
      {isVisible && (
        <iframe
          key={reloadKey}
          src={`https://www.instagram.com/reel/${reel.id}/embed/`}
          className="reel-cover-iframe"
          allowTransparency="true"
          allow="encrypted-media; autoplay; clipboard-write"
          allowFullScreen
          title="Instagram Reel by @foodoscope_api"
          onLoad={() => setIsLoaded(true)}
          loading="lazy"
        />
      )}
      {/* Decorative hover glow only — does not capture clicks */}
      <span className="reel-card-overlay" aria-hidden="true" />
      {/* Replay: remounts the embed back to its cover so it can be watched again */}
      <button
        type="button"
        className="reel-replay-btn"
        onClick={replay}
        aria-label="Replay reel"
        title="Replay"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 2v6h6" />
          <path d="M3.51 15a9 9 0 1 0 2.13-9.36L3 8" />
        </svg>
      </button>
    </div>
  );
}

export default function InstagramReelsSection() {
  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const cardWidth = container.firstChild?.offsetWidth || 0;
    const index = Math.round(container.scrollLeft / (cardWidth + 16));
    setActiveIndex(Math.min(index, REELS.length - 1));
  }, []);

  const scrollToIndex = (index) => {
    const container = scrollRef.current;
    if (!container) return;
    const cardWidth = container.firstChild?.offsetWidth || 0;
    container.scrollTo({ left: index * (cardWidth + 16), behavior: "smooth" });
  };

  return (
    <>
      <style>{`
        .ig-reels-section {
          position: relative;
          background: linear-gradient(180deg, #ffffff 0%, #f6f7f9 100%);
          padding: 5rem 1.5rem;
          overflow: hidden;
        }

        .ig-header {
          text-align: center;
          margin-bottom: 3rem;
        }
        .ig-header h2 {
          color: #1f2937;
          font-family: 'Sometype Mono', monospace;
          font-weight: 700;
          font-size: clamp(1.6rem, 4vw, 2.5rem);
          margin: 0;
        }
        .ig-handle-link {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          margin-top: 0.75rem;
          color: #6b7280;
          font-size: 0.9rem;
          font-weight: 500;
          font-family: 'DM Sans', sans-serif;
          text-decoration: none;
          transition: color 0.2s;
        }
        .ig-handle-link:hover { color: #04c389; }

        /* Desktop: 3-up centered row */
        .reels-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 320px));
          gap: 1.5rem;
          justify-content: center;
          max-width: 1040px;
          margin: 0 auto;
        }

        /* Card */
        .reel-card {
          position: relative;
          display: block;
          width: 100%;
          aspect-ratio: 9 / 16;
          border: 1px solid rgba(0,0,0,0.08);
          border-radius: 18px;
          overflow: hidden;
          background: #141416;
          box-shadow: 0 8px 28px rgba(0,0,0,0.12);
          transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
        }
        .reel-card:hover {
          transform: translateY(-6px);
          border-color: rgba(4,195,137,0.4);
          box-shadow: 0 16px 40px rgba(0,0,0,0.18);
        }

        /* Embed used as cover: header cropped off, footer clipped by card height.
           Interactive so the native play button works on the first click. */
        .reel-cover-iframe {
          position: absolute;
          top: -54px;
          left: 50%;
          transform: translateX(-50%);
          width: 100%;
          height: calc(100% + 220px);
          border: none;
          display: block;
        }

        /* Hover glow only — must not capture clicks */
        .reel-card-overlay {
          position: absolute;
          inset: 0;
          pointer-events: none;
          border-radius: 18px;
          box-shadow: inset 0 0 0 0 rgba(4,195,137,0);
          transition: box-shadow 0.3s ease;
        }
        .reel-card:hover .reel-card-overlay {
          box-shadow: inset 0 0 0 2px rgba(4,195,137,0.35);
        }

        .reel-replay-btn {
          position: absolute;
          top: 10px;
          right: 10px;
          z-index: 4;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          border: none;
          cursor: pointer;
          color: #fff;
          background: rgba(0,0,0,0.45);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transform: scale(0.85);
          transition: opacity 0.25s ease, transform 0.25s ease, background 0.2s ease;
        }
        .reel-card:hover .reel-replay-btn {
          opacity: 1;
          transform: scale(1);
        }
        .reel-replay-btn:hover {
          background: rgba(4,195,137,0.9);
        }

        .reel-loader {
          position: absolute;
          inset: 0;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #141416;
        }
        .reel-spinner {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          border: 3px solid rgba(255,255,255,0.15);
          border-top-color: #04c389;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* CTA */
        .ig-cta-wrap { text-align: center; margin-top: 3rem; }
        .ig-cta {
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.8rem 1.9rem;
          background: #D9FF5B;
          color: #000;
          font-weight: 600;
          font-size: 0.9rem;
          font-family: 'DM Sans', sans-serif;
          border-radius: 999px;
          text-decoration: none;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .ig-cta:hover {
          transform: scale(1.04);
          box-shadow: 0 8px 24px rgba(217,255,91,0.35);
        }

        /* Mobile carousel */
        .reels-carousel { display: none; }
        .reels-carousel-track {
          display: flex;
          gap: 1rem;
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          padding: 0.5rem 0;
        }
        .reels-carousel-track::-webkit-scrollbar { display: none; }
        .reels-carousel-track > .reel-card {
          flex: 0 0 78%;
          max-width: 300px;
          scroll-snap-align: center;
        }
        .carousel-dots {
          display: flex;
          justify-content: center;
          gap: 8px;
          margin-top: 1.25rem;
        }
        .carousel-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          border: none; padding: 0;
          cursor: pointer;
          background: rgba(255,255,255,0.25);
          transition: all 0.3s ease;
        }
        .carousel-dot.active {
          width: 24px;
          border-radius: 4px;
          background: #D9FF5B;
        }

        @media (max-width: 767px) {
          .ig-reels-section { padding: 3.5rem 1rem; }
          .reels-grid { display: none; }
          .reels-carousel { display: block; }
        }
      `}</style>

      <section id="instagram-reels" className="ig-reels-section">
        <motion.div
          className="ig-header"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2>Follow Us on Instagram</h2>
          <a
            href="https://www.instagram.com/foodoscope_api/"
            target="_blank"
            rel="noopener noreferrer"
            className="ig-handle-link"
          >
            @foodoscope_api
          </a>
        </motion.div>

        {/* Desktop / tablet grid */}
        <div className="reels-grid">
          {REELS.map((reel, index) => (
            <motion.div
              key={reel.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.12 }}
              viewport={{ once: true }}
            >
              <ReelCard reel={reel} />
            </motion.div>
          ))}
        </div>

        {/* Mobile carousel */}
        <div className="reels-carousel">
          <div ref={scrollRef} className="reels-carousel-track" onScroll={handleScroll}>
            {REELS.map((reel) => (
              <ReelCard key={reel.id} reel={reel} />
            ))}
          </div>
          <div className="carousel-dots">
            {REELS.map((_, i) => (
              <button
                key={i}
                className={`carousel-dot ${activeIndex === i ? "active" : ""}`}
                onClick={() => scrollToIndex(i)}
                aria-label={`Go to reel ${i + 1}`}
              />
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          className="ig-cta-wrap"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <a
            href="https://www.instagram.com/foodoscope_api/"
            target="_blank"
            rel="noopener noreferrer"
            className="ig-cta"
          >
            View More on Instagram
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 17l9.2-9.2M17 17V7H7" />
            </svg>
          </a>
        </motion.div>
      </section>
    </>
  );
}
